import { useMemo } from 'react';
import { Button } from '~/components/ui/button';
import { Skeleton } from '~/components/ui/skeleton';
import { formatRelativeTime } from '~/lib/relative-time';
import type { DocumentLastChange } from '~/hooks/queries/use-artifact-changes';
import { changeSegments } from './changes-diff';
import { changesHeader, TOO_MANY_CHANGES } from './changes-header';
import { changesUnavailableReason, type ChangesAvailability } from './changes-gate';

/**
 * FRONT-2669 — the Canvas "Changes" view: what Eddy's last edit to this document
 * changed, as read-only text with removed words struck through and added words
 * highlighted. Every branch keys off the query's own state (FRONT-1525): an error
 * is its own message and never an endless spinner; "no earlier version" is the same
 * sentence the toggle's tooltip carries, shown here as visible text (touch has no hover).
 */
export function ChangesView({
  availability,
  data,
  onRetry,
  onRestore,
  restoring = false,
}: {
  availability: ChangesAvailability;
  data: DocumentLastChange | undefined;
  onRetry: () => void;
  /** FRONT-3313: puts the previous version back. The button shows only when this is passed. */
  onRestore?: () => void;
  restoring?: boolean;
}) {
  const reason = changesUnavailableReason(availability);
  // Memoised on the two texts (not on `data`'s identity): a re-render for any other reason never re-diffs.
  const before = data?.available ? data.before : '';
  const after = data?.available ? data.after : '';
  const segments = useMemo(() => (data?.available ? changeSegments(before, after) : []), [data?.available, before, after]);
  if (reason) {
    return (
      <p data-testid="canvas-changes-unavailable" className="p-6 text-sm text-muted-foreground">
        {reason}
      </p>
    );
  }
  if (availability === 'error') {
    return (
      <div role="alert" data-testid="canvas-changes-error" className="p-6 text-sm text-muted-foreground space-y-3">
        <p>Couldn't load the changes.</p>
        <Button type="button" size="sm" variant="outline" onClick={onRetry}>
          Retry
        </Button>
      </div>
    );
  }
  if (availability === 'loading' || !data || !data.available) {
    return (
      <div className="p-6 space-y-3" data-testid="canvas-changes-loading">
        <Skeleton className="h-4 w-1/2" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-3/4" />
      </div>
    );
  }
  const when = formatRelativeTime(data.changedAt);
  const header = changesHeader(when, data.laterEdits === true);
  return (
    <div className="p-6 space-y-3" data-testid="canvas-changes">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-muted-foreground" data-testid="canvas-changes-header">
          {header}
        </p>
        {onRestore && (
          <Button
            type="button"
            size="sm"
            variant="secondary"
            className="shrink-0"
            onClick={onRestore}
            disabled={restoring}
            tooltip={restoring ? 'Restoring…' : undefined}
            data-testid="canvas-changes-restore"
          >
            Restore previous version
          </Button>
        )}
      </div>
      {segments === null ? (
        <p data-testid="canvas-changes-too-many" className="text-sm text-muted-foreground">
          {TOO_MANY_CHANGES}
        </p>
      ) : (
      <div className="whitespace-pre-wrap break-words text-sm text-foreground" data-testid="canvas-changes-text">
        {segments.map((seg, i) =>
          seg.kind === 'removed' ? (
            <del key={i} className="bg-destructive/10 text-destructive line-through">
              {seg.text}
            </del>
          ) : seg.kind === 'added' ? (
            <ins key={i} className="bg-success/15 no-underline">
              {seg.text}
            </ins>
          ) : (
            <span key={i}>{seg.text}</span>
          ),
        )}
      </div>
      )}
    </div>
  );
}
